<template>
  <div>
    <div :class="{ 'is-invalid': infoImage.error !== '' }">
      <input
        ref="inputDrop"
        type="file"
        class="visually-hidden-input d-none"
        :accept="accept"
        :disabled="disabled"
        :multiple="multiple"
        @change="onChange"
      />

      <div class="drop-container d-flex" :class="{'cursor-pointer': !disabled, 'highlight': highlight}" @click="chooseFile">
        <span id='button' class="px-1 d-flex align-items-center">ファイルを選択</span>
        <span id='val' class="h-100 align-items-center d-flex"
              style="flex: 1">{{ infoImage.file_name }}</span>
      </div>
    </div>

    <div
      v-if="infoImage.image == null || infoImage.image === ''"
      @dragenter.prevent="toggleActive" @dragleave.prevent="toggleActive"
      @dragover.prevent
      @drop.prevent="toggleActive"
      :class="{ 'active-dropzone': active, 'is-invalid': infoImage.error !== '',  'cursor-pointer': !disabled}"
      class="dropzone position-relative"
    >
      <div class="mb-1">
        <i class="display-4 text-muted ri-upload-cloud-2-fill"></i>
      </div>

      <h5 class="text-center">Drop files here or click to upload.</h5>
      <label @click="chooseFile" for="dropzoneFile" class="bg-light text-dark stretched-link">Upload</label>

    </div>
    <div v-else>
      <img v-if="infoImage.type === 'IMAGE'" :src="infoImage.image" alt="facilityForm-image" class="w-100 image-preview" :style="{ height: heightPreview }">
      <video v-else class="w-100" style="max-height: 200px" controls>
        <source :src="infoImage.image" :type="infoImage.typeExtension" ref="sourceVideo">
      </video>
    </div>

    <div class="error-text-show" v-if="infoImage.error">
      {{ infoImage.error }}
    </div>
  </div>
</template>


<script>
import { computed, reactive, ref, watch } from "vue";
import i18n from '@/i18n'
import {detectExtensionFile} from "@/state/helpers";
import { generateImageOss } from "@/helpers/axios/formDataRequest";

export default {
  name: "dropzone",
  props: {
    propNameFile: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "drop-file",
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    allowedExtensions: {
      type: Array,
      default: () => ['image/jpeg', 'image/png', 'image/jpg']
    },
    maxFileSize: {
      type: Number,
      default: 10
    },
    showErrorInside: {
      type: Boolean,
      default: true
    },
    highlight: {
      type: Boolean,
      default: false
    },
    field: {
      type: String,
      default: ''
    },
    heightPreview: {
      type: String,
      default: '230px',
      required: false
    },
    defaultImage: {
      type: String,
      default: '',
      required: false
    },
    defaultType: {
      type: String,
      default: 'IMAGE',
      required: false
    },
    resolution: {
      type: String,
      default: '',
      required: false
    }
  },
  setup(props, {emit}) {
    const active = ref(false);
    const state = reactive({
      nameFile: '選択されていません'
    })
    const infoImage = reactive({
      file_name: '',
      image: '',
      file: null,
      error: '',
      type: props.defaultType,
      typeExtension: ''
    })
    
    watch(() => props.defaultType, (newValue) => {
      infoImage.type = newValue;
    })
    
    watch(() => props.defaultImage, (newValue) => {
      if (newValue) {
        const splitName = newValue.split('/') ?? [];
        infoImage.image = generateImageOss(newValue);
        infoImage.file_name = splitName[splitName.length - 1] ?? '';
      } else {
        infoImage.image = '';
        infoImage.file_name = '選択されていません'
      }
    }, { immediate: true });

    const currentField = computed(() => props.field);
    const fieldRef = ref(props.field);
    watch(() => props.field, (val) => fieldRef.value = val);
    
    const inputDrop = ref(null);
    const refreshInput = () => {
      inputDrop.value.value = '';
    }

    const toggleActive = async (e) => {
      if (props.disabled) {
        return;
      }
      active.value = !active.value;
      const file = e.dataTransfer.files[0];
      const check = await validateFile(file, props.resolution);
      if (check) {
        infoImage.file = file;
        infoImage.file_name = file.name;
        infoImage.image = URL.createObjectURL(file);

        emit('selectedFile', { file, fieldName: props.field });
      }
    };

    const onChange = async (e) => {
      if (props.disabled) {
        return;
      }
      const files = e.target.files || e.dataTransfer.files;
      const file = files[0] || null;

      const check = await validateFile(file, props.resolution);
      const type = await detectExtensionFile(file);
      if (check) {
        infoImage.file = file;
        infoImage.file_name = file.name;
        infoImage.image = URL.createObjectURL(file);
        if (type.type) {
          infoImage.type = type.type.toUpperCase();
          infoImage.typeExtension = `video/${type.extension}`;
        }

        emit('selectedFile', { file, fieldName: props.field });
      }
    }
    
    const validateFile = async (file, resolution = '') => {
      if (resolution && file.type.startsWith('image/')) {
          const [expectedWidth, expectedHeight] = resolution.split('x').map(Number);

          const img = new Image();
          const imageLoadPromise = new Promise((resolve, reject) => {
            img.onload = () => resolve({ width: img.width, height: img.height });
            img.onerror = reject;
          });

          img.src = URL.createObjectURL(file);
          
          try {
            const { width, height } = await imageLoadPromise;

            if (width !== expectedWidth || height !== expectedHeight) {
              const errorMessage = i18n.global.t('msg.invalid_resolution', {
                width: expectedWidth,
                height: expectedHeight
              });
              infoImage.error = errorMessage;
              emit('errorUpload', errorMessage);
              return false;
            }
          } catch (err) {
            const errorMessage = i18n.global.t('msg.error_reading_image');
            infoImage.error = errorMessage;
            emit('errorUpload', errorMessage);
            return false;
          }
        }
      
      if (file === null || typeof file === 'undefined') return;
      const type = await detectExtensionFile(file);
      const maxFileSize = type.type === 'video' ? 500 : 10;
      const {allowedExtensions} = props;
      if (allowedExtensions.length > 0 && !allowedExtensions.includes(file.type)) {
        const arrayTemp = props.allowedExtensions.map(extend => {
          const splitText = extend.split('/');
          return splitText.length ? splitText[splitText.length - 1] : extend
        })
        let errorMessage = i18n.global.t('msg.only_accept_file', {acceptFiles: arrayTemp.join(', ')})
        infoImage.error = errorMessage
        emit('errorUpload', errorMessage)

        return false;
      }
      if (file.size / 1024 / 1024 > maxFileSize) {
        let errorMessage = i18n.global.t('t-err-upload-max', {value: maxFileSize});
        infoImage.error = errorMessage
        emit('errorUpload', errorMessage)
        return false;
      }

      infoImage.error = ''
      emit('errorUpload', '')
      return true;
    }
    
    const resetValue = () => {
      infoImage.file_name = '選択されていません';
      infoImage.image = '';
      infoImage.file = null;
      infoImage.error = '';
      infoImage.type = props.defaultType;
      infoImage.typeExtension = '';
      refreshInput();
    }

    return { active, toggleActive, onChange, state, refreshInput, inputDrop, currentField, infoImage, resetValue };
  },
  methods: {
    chooseFile() {
      this.$refs.inputDrop.click();
    }
  }
};
</script>
<style lang="scss" scoped>
.input-drop {
  height: 38px;
  opacity: 0;
  display: none;
}

.drop-container {
  border-radius: 4px;
  height: 38px;
  background-color: white;
  //box-shadow: 1px 2px 3px #ededed;
  position: relative;
  border: 1px solid #d8d8d8;
}

#val {
  height: 25px;
  font-size: 13px;
  line-height: 38px;
  text-indent: 10px;
  pointer-events: none;
  display: -webkit-box !important;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

#button {
  width: 107px;
  display: block;
  background-color: #f0f2f6;
  color: black;
  font-size: 13px;
  line-height: 25px;
  text-align: center;
  -webkit-transition: 500ms all;
  -moz-transition: 500ms all;
  transition: 500ms all;
}
.highlight {
  border-color: #edc86f;
}

.image-preview {
  object-fit: contain;
  object-position: center;
}
</style>
